import { useEffect, useState } from 'react'
import { pushNode, putNode } from '../firebase/rest.js'
import { findDuplicate } from '../utils/fuzzyMatch.js'

const SEARCH_DEBOUNCE_MS = 300
// Lenient threshold for the "is this song already requested?" check —
// false positives are recoverable (user clicks "Request anyway"), so we'd
// rather over-warn than under-warn. Matches the existing duplicate-warning
// threshold used elsewhere.
const DUP_THRESHOLD = 0.82

/**
 * Modal request form. Search-as-you-type Spotify catalog with album art.
 *
 * Flow:
 *   1. Pick which room (Main / Room 2). Late greys out when not eligible.
 *   2. Type a song name. After 300ms idle, /api/search returns up to 8
 *      matching tracks with album art.
 *   3. Tap one to "pick" it. The picked track shows as a card with a clear
 *      button. Submission stores spotify_track_id, name, artists, album_art.
 *      Free-text / "request anyway" is NOT allowed — dancers must pick a
 *      Spotify track from search results before submitting.
 *   4. On submit, fuzzy-match against existing pending/queued requests in
 *      the chosen room. If a match is found, show the existing request and
 *      offer "Vote for it instead" or "Request anyway".
 *   5. Pick swing vs line dance type and submit.
 */
export default function RequestForm({
  defaultRoom,
  deviceId,
  mainAvailable,
  lateAvailable,
  mainQuotas,   // { used, max } — single per-room cap (any dance type)
  lateQuotas,   // { used, max } — single per-room cap (line dance only)
  existingRequests = [],
  onClose,
}) {
  const initialRoom =
    (defaultRoom === 'late' && lateAvailable) ||
    (defaultRoom === 'main' && mainAvailable)
      ? defaultRoom
      : mainAvailable ? 'main' : lateAvailable ? 'late' : defaultRoom

  const [room, setRoom] = useState(initialRoom)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [picked, setPicked] = useState(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  // Default dance type is "line" — most submissions at this venue are
  // line dance requests, and Room 2 forces line anyway. Dancers can
  // toggle to swing in Main Room.
  const [danceType, setDanceType] = useState('line')

  // Room 2 is line-dance only. If the dancer flips the room toggle inside
  // the form to Room 2 while "swing" was selected, auto-promote to "line"
  // so the submission isn't silently invalid.
  useEffect(() => {
    if (room === 'late' && danceType !== 'line') {
      setDanceType('line')
    }
  }, [room, danceType])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  // The duplicate-prompt state: when set, the form swaps to a confirmation
  // view showing the matching existing request with one-tap vote action.
  const [duplicateMatch, setDuplicateMatch] = useState(null)

  useEffect(() => {
    if (picked && query === picked.name) return
    if (query.trim().length < 2) {
      setResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    setSearchError(null)
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(query.trim())}`, {
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`Search failed (${res.status})`)
        const data = await res.json()
        setResults(data.tracks || [])
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSearchError(err.message)
          setResults([])
        }
      } finally {
        setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, picked])

  function handlePick(track) {
    setPicked(track)
    setQuery(track.name)
    setResults([])
  }

  function handleClearPick() {
    setPicked(null)
    setQuery('')
    setResults([])
  }

  function buildCandidate() {
    // Submission is gated on `picked` being set, so these values always
    // come from a real Spotify track.
    return {
      song_title: picked.name,
      artist: picked.artists,
      spotify_track_id: picked.id,
    }
  }

  /**
   * Check if this song is already on the list. Looks at pending, queued,
   * AND played requests in the selected room — each gets its own variant
   * of the duplicate prompt (vote suggestion / "already queued" /
   * "already played"). The dancer always has the option to request anyway.
   *
   * Cross-room rule (matches DancerView.roomRequests):
   *   • Same-room requests always count (any status, any dance type).
   *   • If the candidate is a LINE DANCE, also include line-dance
   *     requests from the OTHER room ONLY when those are already
   *     `played` — so the dancer sees "this line dance was already
   *     played in the other room" and won't repeat it. Pending and
   *     queued line dances stay private to their submitting room.
   *   • Swing candidates only check their own room.
   */
  function findExistingDuplicate() {
    const candidate = buildCandidate()
    const candidateIsLine = danceType === 'line'
    const sameRoom = existingRequests
      .filter((r) => {
        const validStatus =
          r.status === 'pending' || r.status === 'queued' || r.status === 'played'
        if (!validStatus) return false
        if (r.room === room) return true
        if (candidateIsLine && r.dance_type === 'line' && r.status === 'played') return true
        return false
      })
      .map((r) => ({
        ...r,
        voteCount: r.voteCount ?? (r.votes ? Object.keys(r.votes).length : 0),
      }))
    return findDuplicate(candidate, sameRoom, DUP_THRESHOLD)
  }

  async function actuallySubmit() {
    // Defensive: submit is gated on `picked`, but bail out cleanly if
    // somehow called without one (e.g. stale state).
    if (!picked) {
      setSubmitError('Please pick a song from the search results before submitting.')
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      const payload = {
        room,
        device_id: deviceId,
        song_title: picked.name,
        artist: picked.artists,
        spotify_track_id: picked.id,
        album_art: picked.album_art || null,
        dance_type: danceType,
        status: 'pending',
        created_at: Date.now(),
        votes: { [deviceId]: true },
      }
      await pushNode('requests', payload)
      onClose()
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Resolves the per-room quota for the current selection. Quotas are
  // now a single bucket per room — dance type doesn't split it.
  function currentQuota() {
    return (room === 'main' ? mainQuotas : lateQuotas) || { used: 0, max: 0 }
  }

  async function submit(e) {
    e.preventDefault()
    // Hard gate: dancers must select a Spotify track from search results.
    // No free-text / typed-only submissions are allowed anymore.
    if (!picked) {
      setSubmitError('Please pick a song from the search results before submitting.')
      return
    }
    if ((room === 'main' && !mainAvailable) || (room === 'late' && !lateAvailable)) {
      setSubmitError('That room is not currently accepting requests.')
      return
    }
    const cq = currentQuota()
    if (cq.used >= cq.max) {
      setSubmitError(
        `You've used all ${cq.max} of your requests in this room. Try the other room or wait for the night to reset.`,
      )
      return
    }
    // Duplicate check — switch to the duplicate prompt if we find a match.
    const match = findExistingDuplicate()
    if (match) {
      setDuplicateMatch(match)
      return
    }
    await actuallySubmit()
  }

  /** "Vote for it instead" — adds the dancer's vote to the existing request. */
  async function voteForExisting() {
    if (!duplicateMatch) return
    const alreadyVoted = duplicateMatch.votes && duplicateMatch.votes[deviceId]
    setSubmitting(true)
    try {
      if (!alreadyVoted) {
        await putNode(`requests/${duplicateMatch.id}/votes/${deviceId}`, true)
      }
      onClose()
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  /** "Request anyway" — force-submit the duplicate. */
  async function forceSubmit() {
    setDuplicateMatch(null)
    await actuallySubmit()
  }

  // Show a "no match — please refine" hint when the user has typed but
  // nothing came back. We no longer accept free-text submissions, so the
  // hint nudges the dancer to adjust their query rather than offering a
  // submit-anyway escape hatch.
  const showNoMatchHint =
    !picked &&
    !searching &&
    query.trim().length >= 2 &&
    results.length === 0 &&
    !searchError

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 grid place-items-end sm:place-items-center"
      onClick={onClose}
    >
      <form
        className="bg-peak-surface w-full max-w-md p-5 rounded-t-3xl sm:rounded-3xl space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        {duplicateMatch ? (
          <DuplicatePrompt
            match={duplicateMatch}
            deviceId={deviceId}
            submitting={submitting}
            onVote={voteForExisting}
            onForceSubmit={forceSubmit}
            onBack={() => setDuplicateMatch(null)}
            onCancel={onClose}
          />
        ) : (
          <RequestFormContent
            room={room}
            setRoom={setRoom}
            mainAvailable={mainAvailable}
            lateAvailable={lateAvailable}
            mainQuotas={mainQuotas}
            lateQuotas={lateQuotas}
            query={query}
            setQuery={setQuery}
            picked={picked}
            handlePick={handlePick}
            handleClearPick={handleClearPick}
            searching={searching}
            searchError={searchError}
            results={results}
            showNoMatchHint={showNoMatchHint}
            danceType={danceType}
            setDanceType={setDanceType}
            submitting={submitting}
            submitError={submitError}
            currentQuota={currentQuota()}
            onClose={onClose}
          />
        )}
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------

/** The original form content — extracted so it sits cleanly alongside the
 *  duplicate prompt. */
function RequestFormContent({
  room, setRoom,
  mainAvailable, lateAvailable, mainQuotas, lateQuotas,
  query, setQuery, picked, handlePick, handleClearPick,
  searching, searchError, results, showNoMatchHint,
  danceType, setDanceType,
  submitting, submitError, currentQuota, onClose,
}) {
  // The quota label inside each RoomChoice shows the dancer's per-room
  // tally — quotas are no longer split by dance type, so it's just one
  // used/max number per room.
  const roomQuotaFor = (rm) =>
    (rm === 'main' ? mainQuotas : lateQuotas) || { used: 0, max: 0 }
  const atQuota = currentQuota.used >= currentQuota.max
  return (
    <>
      <div className="flex items-baseline justify-between">
        <h2 className="font-bold text-lg">Request a song</h2>
        <button type="button" onClick={onClose} className="text-peak-muted text-sm">
          Cancel
        </button>
      </div>

      <fieldset>
        <span className="text-xs text-peak-muted">Which room?</span>
        <div className="flex gap-2 mt-1">
          <RoomChoice
            id="main"
            label="Main Room"
            accent="bg-peak-accent border-peak-accent"
            selected={room === 'main'}
            available={mainAvailable}
            quota={roomQuotaFor('main')}
            onSelect={() => setRoom('main')}
            disabledReason={mainAvailable ? null : 'Currently not accepting requests'}
          />
          <RoomChoice
            id="late"
            label="Room 2"
            accent="bg-peak-accent2 border-peak-accent2"
            selected={room === 'late'}
            available={lateAvailable}
            quota={roomQuotaFor('late')}
            onSelect={() => setRoom('late')}
            disabledReason={lateAvailable ? null : 'Currently not accepting requests'}
          />
        </div>
      </fieldset>

      <div className="block">
        <span className="text-xs text-peak-muted">Song</span>
        {picked ? (
          <PickedTrack track={picked} onClear={handleClearPick} />
        ) : (
          <>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Start typing a song name…"
              className="w-full mt-1 px-3 py-3 rounded-xl bg-peak-bg border border-white/10 focus:border-peak-accent outline-none"
              maxLength={120}
            />
            <SearchResults
              results={results}
              searching={searching}
              error={searchError}
              onPick={handlePick}
            />
            {showNoMatchHint && (
              <p className="text-xs text-peak-muted mt-2 px-1">
                No matches on Spotify for <strong className="text-white">"{query}"</strong>.
                Try a different spelling or include the artist — only songs picked
                from the search results can be requested.
              </p>
            )}
          </>
        )}
      </div>

      {/* Dance type picker — hidden for Room 2 since it's line-dance only.
          The form auto-flips danceType to "line" when the dancer selects
          Room 2 above (see the useEffect in the parent component). */}
      {room === 'late' ? (
        <p className="text-xs text-peak-muted px-1">
          Room 2 is line dance only — your request will be queued as line dance.
        </p>
      ) : (
        <fieldset>
          <span className="text-xs text-peak-muted">Dance type</span>
          <div className="flex gap-2 mt-1">
            {/* Order: line on the LEFT, swing on the RIGHT — matches the
                rest of the app (instructor desktop columns, dancer tabs). */}
            {['line', 'swing'].map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => setDanceType(opt)}
                className={`flex-1 py-2 rounded-xl border ${
                  danceType === opt
                    ? 'bg-peak-accent border-peak-accent text-white'
                    : 'bg-peak-bg border-white/10 text-peak-muted'
                }`}
              >
                {opt === 'swing' ? 'Swing' : 'Line dance'}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {atQuota && (
        <p className="text-xs text-peak-warn px-1">
          You've used all {currentQuota.max} of your requests for this room. Try the other room or wait for the next night.
        </p>
      )}
      {submitError && <p className="text-sm text-red-300">{submitError}</p>}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={!picked || submitting || atQuota}
      >
        {submitting
          ? 'Submitting…'
          : picked
            ? `Submit "${picked.name}"`
            : 'Pick a song from search results'}
      </button>
    </>
  )
}

// ---------------------------------------------------------------------------

function DuplicatePrompt({ match, deviceId, submitting, onVote, onForceSubmit, onBack, onCancel }) {
  const alreadyVoted = match.votes && match.votes[deviceId]
  const isQueued = match.status === 'queued'
  const isPlayed = match.status === 'played'

  // Heading varies by status so the dancer immediately knows the situation
  const heading = isPlayed
    ? 'Already played tonight'
    : isQueued
      ? 'Already queued'
      : 'Already on the list'

  // Body copy varies by the four cases: played / queued / pending+already-voted / pending+not-voted
  const body = isPlayed
    ? 'This song was already played tonight. Are you sure you want to request it again?'
    : isQueued
      ? 'This song is already queued — the instructor will play it soon. You can wait, or request it again anyway.'
      : alreadyVoted
        ? 'This song is already on the list and you\'ve voted for it. No need to request it again.'
        : 'This song is already on the list. Want to vote for it instead of submitting a duplicate?'

  // Voting only makes sense for pending requests the dancer hasn't already voted on
  const canVote = !isQueued && !isPlayed && !alreadyVoted

  return (
    <>
      <div className="flex items-baseline justify-between">
        <h2 className="font-bold text-lg">{heading}</h2>
        <button type="button" onClick={onCancel} className="text-peak-muted text-sm">
          Cancel
        </button>
      </div>

      <p className="text-sm text-peak-muted">{body}</p>

      <ExistingRequestCard request={match} />

      <div className="flex flex-col gap-2">
        {canVote && (
          <button
            type="button"
            onClick={onVote}
            disabled={submitting}
            className="btn-primary w-full"
          >
            {submitting ? 'Voting…' : 'Vote for this one'}
          </button>
        )}
        {alreadyVoted && !isPlayed && !isQueued && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-primary w-full"
          >
            Got it — close
          </button>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBack}
            className="btn-secondary flex-1 text-sm py-2"
          >
            ← Back to search
          </button>
          <button
            type="button"
            onClick={onForceSubmit}
            disabled={submitting}
            className="btn-secondary flex-1 text-sm py-2"
          >
            Request anyway
          </button>
        </div>
      </div>
    </>
  )
}

function ExistingRequestCard({ request }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-peak-bg border border-white/10">
      {request.album_art ? (
        <img
          src={request.album_art}
          alt=""
          className="w-12 h-12 rounded object-cover flex-none"
        />
      ) : (
        <div className="w-12 h-12 rounded bg-white/5 grid place-items-center flex-none text-peak-muted">
          ♪
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{request.song_title}</p>
        <p className="text-xs text-peak-muted truncate">
          {request.artist || 'no artist'}
          {request.status === 'queued' && <span className="ml-1 text-peak-warn">· queued</span>}
          {request.status === 'played' && <span className="ml-1 text-peak-ok">· played</span>}
        </p>
      </div>
      {/* No vote count here — vote totals are instructor-only on the
          dancer side of the app. */}
    </div>
  )
}

// ---------------------------------------------------------------------------

function PickedTrack({ track, onClear }) {
  return (
    <div className="mt-1 flex items-center gap-3 px-3 py-2 rounded-xl bg-peak-bg border border-peak-accent/40">
      {track.album_art ? (
        <img src={track.album_art} alt="" className="w-12 h-12 rounded object-cover flex-none" />
      ) : (
        <div className="w-12 h-12 rounded bg-peak-accent/20 grid place-items-center flex-none">♪</div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{track.name}</p>
        <p className="text-xs text-peak-muted truncate">{track.artists}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-peak-muted hover:text-white px-2 py-1 flex-none"
      >
        Change
      </button>
    </div>
  )
}

function SearchResults({ results, searching, error, onPick }) {
  if (error) {
    return (
      <p className="text-xs text-red-300 mt-2 px-1">
        Couldn't reach Spotify search ({error}). Try again in a moment — songs
        must be picked from search results to submit.
      </p>
    )
  }
  if (searching && results.length === 0) {
    return <p className="text-xs text-peak-muted mt-2 px-1">Searching…</p>
  }
  if (results.length === 0) return null
  return (
    <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
      {results.map((track) => (
        <li key={track.id}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onPick(track)
            }}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 active:bg-white/10 text-left"
          >
            {track.album_art ? (
              <img
                src={track.album_art}
                alt=""
                className="w-10 h-10 rounded object-cover flex-none"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-peak-accent/20 grid place-items-center flex-none">♪</div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{track.name}</p>
              <p className="text-xs text-peak-muted truncate">{track.artists}</p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}

function RoomChoice({ label, accent, selected, available, quota, onSelect, disabledReason }) {
  return (
    <button
      type="button"
      onClick={available ? onSelect : undefined}
      disabled={!available}
      title={disabledReason || ''}
      className={`flex-1 py-2 px-2 rounded-xl border text-sm ${
        selected
          ? `${accent} text-white`
          : available
            ? 'bg-peak-bg border-white/10 text-peak-muted hover:text-white'
            : 'bg-peak-bg border-white/5 text-peak-muted/40 cursor-not-allowed'
      }`}
    >
      <div className="font-semibold leading-tight">{label}</div>
      <div className="text-[10px] opacity-80 mt-0.5">
        {available
          ? `${quota.used}/${quota.max} requests`
          : 'unavailable'}
      </div>
    </button>
  )
}
